// Regras puras do formulário de campanha (decisão 28). Ficam fora do componente
// de propósito: o formulário antigo virou um arquivo de 2.523 linhas justamente
// porque a regra morava no meio do template. Aqui nada importa Vue, nada chama
// API e tudo é testável direto.

// Ritmo em três nomes. Cada um é um par de intervalos reais do drip (segundos
// entre um envio e o próximo) mais a janela de horário e a cota diária.
export const PACE_PRESETS = {
  fast: {
    minIntervalS: 5,
    maxIntervalS: 15,
    windowStart: 8,
    windowEnd: 20,
    dailyLimit: 500,
  },
  normal: {
    minIntervalS: 40,
    maxIntervalS: 90,
    windowStart: 9,
    windowEnd: 18,
    dailyLimit: 200,
  },
  safe: {
    minIntervalS: 90,
    maxIntervalS: 240,
    windowStart: 9,
    windowEnd: 17,
    dailyLimit: 80,
  },
};

export const PACE_ORDER = ['fast', 'normal', 'safe'];

export const paceConfig = pace => PACE_PRESETS[pace] || PACE_PRESETS.normal;

// Estimativa de duração DE VERDADE: a média do intervalo vezes os intervos entre
// os envios (N pessoas têm N-1 esperas), e quantos dias a cota diária impõe.
export const estimateDuration = (recipientCount, pace) => {
  const count = Number(recipientCount) || 0;
  const config = paceConfig(pace);
  if (count <= 0) return { minutes: 0, days: 0 };

  const averageInterval = (config.minIntervalS + config.maxIntervalS) / 2;
  const days = Math.ceil(count / config.dailyLimit);
  const perDay = Math.min(count, config.dailyLimit);
  const seconds = Math.max(perDay - 1, 0) * averageInterval;
  // Arredonda pra cima em blocos de 5 minutos: "cerca de 45 minutos" é uma
  // promessa honesta; "40,8 minutos" é uma precisão que não existe.
  const minutes = Math.ceil(seconds / 60 / 5) * 5;
  return { minutes, days };
};

// Motivos de exclusão devolvidos pela prévia do público, na ordem em que fazem
// sentido pra quem lê ("resolvo o telefone" vem antes de "é duplicado").
export const EXCLUSION_REASONS = [
  'no_contact',
  'no_phone',
  'channel_incompatible',
  'opted_out',
  'duplicate',
];

// Como cada motivo é dito na frase dos de fora. "sem contato ligado ao cartão" é
// o cartão marcado que não tem contato nenhum: ele sumia em silêncio da prévia.
export const EXCLUSION_REASON_LABELS = {
  no_contact: 'sem contato ligado ao cartão',
  no_phone: 'sem telefone',
  channel_incompatible: 'número que este canal não alcança',
  opted_out: 'pediu para não receber',
  duplicate: 'já está nesta campanha',
};

// "2 sem telefone e 1 pediu para não receber". Segue a ordem de EXCLUSION_REASONS,
// não a ordem em que o servidor devolveu as chaves.
export const excludedReasonsSentence = (reasons = {}) => {
  const parts = EXCLUSION_REASONS.filter(
    reason => Number(reasons?.[reason]) > 0
  ).map(reason => `${reasons[reason]} ${EXCLUSION_REASON_LABELS[reason]}`);
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
};

// Quantas PESSOAS os cartões marcados viraram: quem recebe mais quem ficou de
// fora por um motivo de pessoa (o cartão sem contato não é pessoa nenhuma).
export const peopleFromPreview = preview => {
  if (!preview) return 0;
  const excluded = Number(preview.excluded) || 0;
  const noContact = Number(preview.reasons?.no_contact) || 0;
  return (Number(preview.receiving) || 0) + Math.max(excluded - noContact, 0);
};

// "12 cartões, 7 pessoas". Só aparece no público de cartões e só quando os dois
// números diferem: dois cartões do mesmo contato são a mesma pessoa, e isso
// precisa ficar dito em vez de virar um sumiço.
export const cardsVsPeopleSentence = (preview, audienceType) => {
  if (audienceType !== 'kanban_items') return '';
  const cards = Number(preview?.cards_considered) || 0;
  if (!cards) return '';
  const people = peopleFromPreview(preview);
  if (people === cards) return '';
  return `${cards} ${cards === 1 ? 'cartão' : 'cartões'}, ${people} ${
    people === 1 ? 'pessoa' : 'pessoas'
  }`;
};

export const audienceRequiresPreview = audienceType => audienceType !== 'csv';

// ===== Público =====
// Monta o bloco `audience` da API a partir do estado do formulário.
export const buildAudience = (state, { csvContacts = [] } = {}) => {
  switch (state.audienceType) {
    case 'kanban_items':
      return [
        {
          type: 'KanbanItems',
          item_ids: (state.kanbanItemIds || []).map(Number).filter(Boolean),
        },
      ];
    case 'labels':
      return (state.selectedAudience || []).map(id => ({ id, type: 'Label' }));
    case 'conversation_labels':
      return (state.selectedAudience || []).map(id => ({
        id,
        type: 'ConversationLabel',
      }));
    case 'kanban':
      return [
        {
          type: 'KanbanStage',
          funnel_id: state.selectedFunnel,
          stage_id: state.selectedStage,
        },
      ];
    case 'funnel':
      return [{ type: 'Funnel', funnel_id: state.selectedFunnel }];
    case 'assigned_agent':
      return [{ type: 'AssignedAgent', agent_id: state.assignedAgentId }];
    case 'conversation_status':
      return [{ type: 'ConversationStatus', status: state.conversationStatus }];
    case 'contact_attribute':
      return [
        {
          type: 'ContactAttribute',
          key: state.contactAttrKey,
          value: state.contactAttrValue,
        },
      ];
    case 'csv':
      return csvContacts.map(contact => ({
        type: 'PhoneNumber',
        phone_number: contact.phone,
        name: contact.name,
        params: contact.params || {},
      }));
    default:
      return [];
  }
};

// ===== Validação por bloco =====
// Cada pendência diz a QUAL bloco pertence (o resumo destaca o bloco) e traz a
// chave de tradução do motivo. A ordem é a ordem dos blocos na tela, então a
// primeira pendência é sempre a que a pessoa resolve primeiro.
export const validateBlocks = (state, context = {}) => {
  const {
    isFreeText = false,
    channelMode = 'official',
    csvCount = 0,
  } = context;
  const issues = [];
  const add = (block, reason) => issues.push({ block, reason });

  if (!state.inboxId) add(1, 'INBOX');

  if (state.audienceType === 'kanban_items') {
    if (!(state.kanbanItemIds || []).length) add(2, 'KANBAN_ITEMS');
  } else if (['labels', 'conversation_labels'].includes(state.audienceType)) {
    if (!(state.selectedAudience || []).length) add(2, 'AUDIENCE_LABELS');
  } else if (state.audienceType === 'kanban') {
    if (!state.selectedFunnel || !state.selectedStage) add(2, 'AUDIENCE_STAGE');
  } else if (state.audienceType === 'funnel') {
    if (!state.selectedFunnel) add(2, 'AUDIENCE_FUNNEL');
  } else if (state.audienceType === 'assigned_agent') {
    if (!state.assignedAgentId) add(2, 'AUDIENCE_AGENT');
  } else if (state.audienceType === 'contact_attribute') {
    if (!state.contactAttrKey) add(2, 'AUDIENCE_ATTRIBUTE');
  } else if (state.audienceType === 'csv') {
    if (!csvCount) add(2, 'AUDIENCE_CSV');
  }

  if (isFreeText) {
    if (!state.wahaMessage || !state.wahaMessage.trim()) add(3, 'MESSAGE');
  } else if (!state.templateId) {
    add(3, 'TEMPLATE');
  } else if (context.templateParamsMissing) {
    add(3, 'TEMPLATE_PARAMS');
  }

  if (state.scheduleMode === 'later' && !state.scheduledAt) {
    add(4, 'SCHEDULED_AT');
  }

  if (channelMode === 'sms' && state.kaEnabled) {
    if (!state.kaTargetInboxId) add(4, 'KA_INBOX');
    else if (context.kaTargetIsFreeText && !(state.kaMessage || '').trim()) {
      add(4, 'KA_MESSAGE');
    } else if (!context.kaTargetIsFreeText && !state.kaTemplateId) {
      add(4, 'KA_TEMPLATE');
    }
  }

  // O nome da campanha mora na tela de conferir (bloco 5), não no "por onde
  // sai": quem entra pelo cartão "Nova ação de marketing" pula o passo 1, e um
  // campo obrigatório escondido num passo que não aparece travaria o envio para
  // sempre. Conferir também é onde o nome faz companhia ao "Salvar rascunho".
  // Fica no fim da lista para a ordem das pendências continuar sendo a ordem
  // das telas.
  if (!state.title || !state.title.trim()) add(5, 'TITLE');

  return issues;
};

// ===== Passo a passo (decisão 30) =====
// Um bloco por tela. O número do passo é o mesmo número do bloco que a validação
// já usava, então a pendência continua sabendo sozinha a que tela pertence.
export const CAMPAIGN_STEPS = [
  { id: 'channel', block: 1, title: 'Por onde sai' },
  { id: 'audience', block: 2, title: 'Para quem' },
  { id: 'message', block: 3, title: 'O que diz' },
  { id: 'schedule', block: 4, title: 'Quando e em que ritmo' },
  { id: 'review', block: 5, title: 'Conferir e enviar' },
];

// Quem chega com o canal e a caixa já resolvidos não vê o passo 1: não sobrou
// nada para perguntar nele.
export const visibleSteps = ({ skipChannelStep = false } = {}) =>
  CAMPAIGN_STEPS.filter(step => !(skipChannelStep && step.id === 'channel'));

// A pendência que trava ESTE passo. Na tela de conferir, qualquer pendência que
// tenha sobrado trava, inclusive as dos passos anteriores: é a última porta.
export const blockingIssueForStep = (
  state,
  context = {},
  stepId = 'channel'
) => {
  const issues = validateBlocks(state, context);
  if (stepId === 'review') return issues[0] || null;
  const step = CAMPAIGN_STEPS.find(item => item.id === stepId);
  if (!step) return null;
  return issues.find(issue => issue.block === step.block) || null;
};

// O botão fica desabilitado dizendo O QUE falta: a primeira pendência, sempre.
export const firstBlockingIssue = (state, context = {}) =>
  validateBlocks(state, context)[0] || null;

// ===== Prévia da mensagem =====
// Resolve as variáveis do texto para uma pessoa REAL do público (a primeira que a
// prévia do servidor devolveu). Sem pessoa, cai no fallback de nome configurado e
// depois no genérico — nunca inventa um "Maria" que não está na lista.
export const resolveMessageForRecipient = (text, recipient, options = {}) => {
  const fallback = (options.nameFallback || '').trim();
  const fullName = (recipient && recipient.name) || fallback || '';
  const firstName = fullName ? fullName.split(/\s+/)[0] : '';
  const phone = (recipient && recipient.phone_number) || '';

  let out = (text || '').toString();
  const substitutions = {
    primeiro_nome: firstName,
    nome: fullName,
    telefone: phone,
  };
  Object.entries(substitutions).forEach(([key, value]) => {
    out = out.split(`{${key}}`).join(value);
  });
  // Spintax: a prévia mostra a primeira opção, que é o que o primeiro envio usa.
  out = out.replace(
    /\{([^{}|]+\|[^{}]+)\}/g,
    (_, group) => group.split('|')[0]
  );
  // Campo numerado ainda sem valor ({{1}} do modelo ou coluna da planilha):
  // fica explícito, não fica cru. "campo" serve para os dois casos.
  out = out.replace(/\{\{(\d+)\}\}/g, (_, index) => `(campo ${index})`);
  return out.replace(/\s{2,}/g, ' ').trim();
};

// ===== Payload =====
// Porta fiel do `prepareCampaignDetails` do formulário antigo: mesmo contrato com
// o backend (nada de campo novo escondido), agora como função pura.
const buildAssignment = state => {
  const useAi = state.assignmentMode === 'ai' && state.aiAssistantId;
  const assignment = {
    strategy: useAi ? 'none' : state.assignmentStrategy,
    config: {},
  };
  if (useAi) return assignment;

  if (state.assignmentStrategy === 'fixed') {
    assignment.config.agent_id = state.assignmentAgentId;
  } else if (state.assignmentStrategy === 'round_robin') {
    assignment.config.agent_ids = state.assignmentAgentIds;
  } else if (state.assignmentStrategy === 'team') {
    assignment.config.team_id = state.assignmentTeamId;
  } else if (state.assignmentStrategy === 'region_ddd') {
    assignment.config.ddd_map = (state.dddMap || []).reduce((acc, row) => {
      if (row.ddd && row.agentId) acc[String(row.ddd)] = row.agentId;
      return acc;
    }, {});
    assignment.config.fallback_agent_id = state.regionFallbackAgentId;
  }
  return assignment;
};

export const parseKeywords = raw =>
  (raw || '')
    .split(/[,\n;]+/)
    .map(keyword => keyword.trim())
    .filter(Boolean);

const buildMessageVariants = (state, channelMode) => {
  const isWaha = channelMode === 'waha';
  const variants = [
    {
      text: (state.wahaMessage || '').trim(),
      media_blob_id: (isWaha && state.attachedMedia?.blobId) || null,
    },
  ];
  (state.messageVariants || []).forEach(variant => {
    const text = (variant.text || '').trim();
    const media = (isWaha && variant.media?.blobId) || null;
    if (text || media) variants.push({ text, media_blob_id: media });
  });
  const hasMedia = variants.some(variant => variant.media_blob_id);
  return variants.length > 1 || hasMedia ? variants : null;
};

export const buildCampaignPayload = (state, context = {}) => {
  const {
    channelMode = 'official',
    isFreeText = false,
    templateParams = null,
    templateContent = '',
    templateVariants = [],
    keywordTemplateParams = null,
    keywordRenderedMessage = '',
    kaTargetIsFreeText = false,
    csvContacts = [],
  } = context;

  const pace = paceConfig(state.pace);
  const details = {
    title: state.title,
    message: isFreeText ? state.wahaMessage : templateContent,
    template_params: isFreeText ? {} : templateParams || {},
    inbox_id: state.inboxId,
    scheduled_at:
      state.scheduleMode === 'later' && state.scheduledAt
        ? new Date(state.scheduledAt).toISOString()
        : null,
    audience_type: state.audienceType,
    audience_description: (state.audienceDescription || '').trim() || null,
    audience: buildAudience(state, { csvContacts }),
  };

  details.drip_config = {
    // A cota diária e a janela vêm do ritmo, mas o opcional pode sobrescrever:
    // quem abriu o avançado e digitou um limite quis aquele limite.
    daily_limit: Number(state.dailyLimit ?? pace.dailyLimit) || pace.dailyLimit,
    window_start: Number(state.windowStart ?? pace.windowStart),
    window_end: Number(state.windowEnd ?? pace.windowEnd),
    days_of_week: Array.isArray(state.daysOfWeek)
      ? [...state.daysOfWeek].sort((a, b) => a - b)
      : [],
    min_interval_s: pace.minIntervalS,
    max_interval_s: pace.maxIntervalS,
    assignment: buildAssignment(state),
    opt_out_footer: isFreeText ? state.optOutFooter : false,
    name_fallback: (state.nameFallback || '').trim(),
    channel_mode: channelMode,
    spintax_enabled: isFreeText ? state.spintaxEnabled : false,
  };

  if (state.assignmentMode === 'ai' && state.aiAssistantId) {
    details.drip_config.ai_agent = {
      assistant_id: state.aiAssistantId,
      mode: state.requireHumanResponse ? 'suggest_auto' : 'autonomous_suggest',
    };
  }

  if (state.requireHumanResponse) {
    details.drip_config.response_handling = 'copilot';
    if (state.kanbanDestFunnelId && state.kanbanDestStageId) {
      details.drip_config.kanban_destination = {
        funnel_id: state.kanbanDestFunnelId,
        stage_id: state.kanbanDestStageId,
      };
    }
  }

  if (channelMode === 'sms' && state.kaEnabled && state.kaTargetInboxId) {
    const keywordActivation = {
      enabled: true,
      target_inbox_id: state.kaTargetInboxId,
      keywords: parseKeywords(state.kaCustomKeywords),
      reset_and_reassign: state.kaResetReassign,
    };
    if (kaTargetIsFreeText) {
      keywordActivation.message = state.kaMessage;
    } else {
      keywordActivation.message = keywordRenderedMessage || '';
      keywordActivation.template_params = keywordTemplateParams || {};
    }
    details.drip_config.keyword_activation = keywordActivation;
  }

  if (isFreeText) {
    const variants = buildMessageVariants(state, channelMode);
    if (variants) details.drip_config.message_variants = variants;
  } else if (templateVariants.length > 1) {
    details.drip_config.template_variants = templateVariants;
  }

  return details;
};

// ===== Planilha =====
// Normaliza telefone para o formato da API do WhatsApp (dígitos com DDI).
// Porte fiel do formulário antigo, agora testável fora do componente.
export const normalizePhone = raw => {
  let digits = (raw || '').replace(/\D/g, '');
  if (digits.length > 11 && digits.startsWith('55')) digits = digits.slice(2);
  digits = digits.replace(/^0+/, '');
  if (digits.length === 10 && /[6-9]/.test(digits.charAt(2))) {
    digits = `${digits.slice(0, 2)}9${digits.slice(2)}`;
  }
  if (digits.length <= 11) digits = `55${digits}`;
  return digits;
};

const splitRow = line =>
  line.split(/[,;\t]/).map(cell => cell.trim().replace(/^"+|"+$/g, ''));

export const parseCsvContacts = text => {
  const lines = (text || '').split(/\r?\n/).filter(line => line.trim());
  const contacts = [];
  const invalid = [];
  if (!lines.length) return { contacts, invalid };

  const headerCells = splitRow(lines[0]).map(cell => cell.toLowerCase());
  const findColumn = keys =>
    headerCells.findIndex(cell => keys.some(key => cell.includes(key)));
  let phoneIdx = findColumn([
    'telefone',
    'celular',
    'whatsapp',
    'fone',
    'phone',
  ]);
  const nameIdx = findColumn(['nome', 'cliente', 'contato', 'name']);
  const hasHeader = phoneIdx !== -1 || nameIdx !== -1;
  if (phoneIdx === -1) phoneIdx = 0;

  for (let i = hasHeader ? 1 : 0; i < lines.length; i += 1) {
    const cols = splitRow(lines[i]);
    const rawPhone = (cols[phoneIdx] || '').trim();
    if (rawPhone) {
      let name = ((nameIdx !== -1 ? cols[nameIdx] : cols[1]) || '').trim();
      if (name && name === name.toUpperCase()) {
        name = name
          .toLowerCase()
          .replace(
            /(^|[\s\-'])(\p{L})/gu,
            (_, sep, ch) => sep + ch.toUpperCase()
          );
      }
      const params = {};
      if (phoneIdx === 0) {
        for (let c = 2; c < cols.length; c += 1) {
          const value = (cols[c] || '').trim();
          if (value) params[String(c - 1)] = value;
        }
      }
      const phone = normalizePhone(rawPhone);
      if (phone.length >= 12 && phone.length <= 15) {
        contacts.push({ phone, name, original: rawPhone, params });
      } else {
        invalid.push(rawPhone);
      }
    }
  }
  return { contacts, invalid };
};

// Estado inicial do formulário. Fica aqui para o spec montar o mesmo estado que a
// tela monta, sem copiar a lista de campos.
export const createInitialState = () => ({
  title: '',
  audienceDescription: '',
  inboxId: null,
  channelType: 'waha', // waha | official | sms
  templateId: null,
  templateVariants: [],
  scheduleMode: 'now', // now | later
  scheduledAt: null,
  pace: 'normal',
  selectedAudience: [],
  audienceType: 'labels',
  kanbanItemIds: [],
  selectedFunnel: null,
  selectedStage: null,
  assignedAgentId: null,
  conversationStatus: 'open',
  contactAttrKey: '',
  contactAttrValue: '',
  dailyLimit: null,
  windowStart: null,
  windowEnd: null,
  daysOfWeek: [],
  wahaMessage: '',
  messageVariants: [],
  spintaxEnabled: false,
  assignmentMode: 'agent',
  assignmentStrategy: 'none',
  assignmentAgentId: null,
  assignmentAgentIds: [],
  assignmentTeamId: null,
  dddMap: [],
  regionFallbackAgentId: null,
  aiAssistantId: null,
  optOutFooter: true,
  nameFallback: '',
  kaEnabled: false,
  kaTargetInboxId: null,
  kaCustomKeywords: '',
  kaMessage: '',
  kaTemplateId: null,
  kaResetReassign: false,
  attachedMedia: null,
  mediaUploading: false,
  requireHumanResponse: false,
  kanbanDestFunnelId: null,
  kanbanDestStageId: null,
});
